import { Separator, useTheme } from '@fluentui/react';
import { DonutChart, IChartDataPoint } from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPStack, IPText } from 'baseComponents';
import { SingleLegend } from 'components/Charts/PieChart';
import TextInfo from 'components/Notification/Info';
import { globalStackTokensGapMedium, globalTextStylesBold } from 'globalStyles';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { sortOnString } from 'utils/sorting';
import { overflow } from 'utils/string';

interface ITaskTagOverviewProps {
  tagCounts?: Record<number, number>;
  isUpdating: boolean;
  getTaskLinkForTag: (tagId?: number) => string | undefined;
  showSeparator?: boolean;
  info?: string; // overrides the default info text, which refers to control effectiveness
}

interface ITaskTagDataPoint extends IChartDataPoint {
  tagId?: number;
}

export const TaskTagOverview = (props: ITaskTagOverviewProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'control']);
  const theme = useTheme();
  const dashboardHeight = 140;

  const donutChartData = useMemo((): ITaskTagDataPoint[] => {
    if (!props.tagCounts) return [];

    const uniqueTagIds = Object.keys(props.tagCounts)
      .map((id) => parseInt(id))
      .filter((id) => id !== 0);
    const uniqueTags = appContext.globalDataCache.tags.getItemsForId(uniqueTagIds);
    uniqueTags.sort((a, b) => sortOnString(a.value(), b.value()));

    const graphData: ITaskTagDataPoint[] = uniqueTags.map((tag) => ({
      legend: tag.value(),
      data: props.tagCounts?.[tag.tagId] ?? 0,
      color: tag.tagColor,
      tagId: tag.tagId,
    }));

    if ((props.tagCounts[0] ?? 0) > 0) {
      graphData.unshift({
        legend: t('control:TabDashboard.TaskOverviewNoTags'),
        data: props.tagCounts[0],
        color: theme.semanticColors.disabledBackground,
      });
    }

    return graphData;
  }, [appContext.globalDataCache.tags, props.tagCounts, t, theme.semanticColors.disabledBackground]);

  const donutValue = useMemo(
    () => Object.values(props.tagCounts ?? {}).reduce((sum, count) => sum + count, 0),
    [props.tagCounts],
  );

  return (
    <IPStack horizontal tokens={globalStackTokensGapMedium}>
      {props.showSeparator && <Separator vertical styles={{ root: { width: 1, height: dashboardHeight } }} />}
      <IPStack tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <TextInfo message={props.info ?? t('control:TabDashboard.TaskOverviewInfo')}>
            <IPText variant="mediumPlus" styles={globalTextStylesBold}>
              {t('control:TabDashboard.TaskOverview')}
            </IPText>
          </TextInfo>
        </IPStack.Item>
        <IPStack styles={{ root: { maxHeight: dashboardHeight - 40, overflowY: 'auto', paddingRight: 5 } }}>
          {donutChartData
            .filter((dataPoint) => dataPoint.tagId !== undefined)
            .map((dataPoint) => (
              <IPStack.Item key={dataPoint.tagId}>
                <SingleLegend
                  name={overflow(dataPoint.legend || '', 35)}
                  value={dataPoint.data ?? 0}
                  color={dataPoint.color}
                  link={props.getTaskLinkForTag(dataPoint.tagId)}
                />
              </IPStack.Item>
            ))}
          {!props.isUpdating && donutChartData.length === 0 && (
            <IPStack.Item>
              <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
            </IPStack.Item>
          )}
        </IPStack>
      </IPStack>
      {donutChartData.length > 0 && (
        <IPStack.Item>
          <DonutChart
            data={{ chartData: donutChartData }}
            valueInsideDonut={donutValue}
            innerRadius={45}
            height={160}
            width={160}
            roundCorners
            hideLegend
          />
        </IPStack.Item>
      )}
    </IPStack>
  );
};
